// Shared
import { getTextWidth } from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';
import ExportFilter from '../components/listing/ExportFilter/ExportFilter';

// Taxonomies
import { occurrenceDescriptionCore, occurrenceDescriptionFilter } from '../taxonomies/occurrenceDescription';
import { salesTypeNameCore, salesTypeNameFilter } from '../taxonomies/salesTypeName';
import { commonStatusPointCore, commonStatusPointFilter } from '../taxonomies/commonStatusPoint';
import { brandStatusDescriptionCore, brandStatusDescriptionFilter } from '../taxonomies/brandStatusDescription';
import { modelCore, modelFilter } from '../taxonomies/model';
import { modelYearCore, modelYearFilter } from '../taxonomies/modelYear';

// Vista Order core
export function vistaOrderCore() {
  const endpoint = '/vistaorder';
  const endpointFiltersModalExport = '/VistaOrder/ExportVistaOrder'
  const endpointDescriptionVersion = '/descriptionversion/type/vistaorder/id';
  const endpointPriceVersion = null;
  const detailId = 'vistaorderId';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  return {
    endpoint,
    endpointDescriptionVersion,
    endpointFiltersModalExport,
    endpointPriceVersion,
    detailId,
    getDetailId: getDetailId
  };
}

// Vista Order listing
export function vistaOrderListing() {

  // Initial sort
  const initialSort = { field: 'vistaOrderID', sort: 'asc' };

  // Initial filter values
  const initialFilters = { MetaOrderSearchType: 'C', PinNumberSearchType: 'C', VinSearchType: 'C', CountryTwoLetters: 'US' };

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="data-type"
                    id={params.row.realId}
                    endpoint="/vista-order"
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            {
              field: 'id',
              headerName: 'Row',
              hide: true,
              hideFromColumnsSelector: true
            },
            {
              field: 'realId',
              headerName: 'ID',
              hide: true
            },
            { field: 'country', headerName: 'Country' },
            { field: 'commonOrderNumber', headerName: 'Common Order Number'},
            { field: 'occurrenceCode', headerName: 'Occurrence Code' },
            { field: 'occurrenceDescription', headerName: 'Occurrence Description' },
            { field: 'lastAmendedDate', headerName: 'Last Amended Date' },
            { field: 'commonStatusPoint', headerName: 'Common Status Point'},
            { field: 'brandStatusDescription', headerName: 'Brand Status Description'},
            { field: 'factoryOrderNumber', headerName: 'Factory Order Number' },
            { field: 'year', headerName: 'Model Year' },
            { field: 'salesTypeCode', headerName: 'Sales Type Code' },
            { field: 'salesTypeName', headerName: 'Sales Type Name' },
            { field: 'salesTypeSecondHandoverCode', headerName: 'Sales Type Second Handover Code' },
            { field: 'salesTypeSecondHandoverName', headerName: 'Sales Type Second Handover Name' },
            { field: 'pinNumber', headerName: 'Pin Number' },
            { field: 'vin', headerName: 'Vin' },
            { field: 'soldReportingDate', headerName: 'Sold Reporting Date' },
            { field: 'dealerCode', headerName: 'Dealer Code' },
            { field: 'VPID', headerName: 'VPID' },
            { field: 'PNO12', headerName: 'PNO12' },
            { field: 'model', headerName: 'Model' },
            { field: 'trim', headerName: 'Trim' },
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          let text = row[id];

          // Add extra chars that might appear on the price
          if (id === 'price' || id === 'retailerNet') {
            text += '$,.00';
          }

          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: i,
        realId: row.vistaOrderID,
        commonOrderNumber: row.commonOrderNumber,
        occurrenceCode: row.occurrenceCode,
        occurrenceDescription: row.occurrenceDescription,
        lastAmendedDate: row.lastAmendedDate,
        commonStatusPoint: row.commonStatusPoint,
        brandStatusDescription: row.brandStatusDescription,
        factoryOrderNumber: row.factoryOrderNumber,
        country: row.country,
        year: row.year,
        salesTypeCode: row.salesTypeCode,
        salesTypeName: row.salesTypeName,
        salesTypeSecondHandoverCode: row.salesTypeSecondHandoverCode,
        salesTypeSecondHandoverName: row.salesTypeSecondHandoverName,
        pinNumber: row.pinNumber,
        vin: row.vin,
        soldReportingDate: row.soldReportingDate,
        dealerCode: row.dealerCode,
        VPID: row.vpid,
        PNO12: row.pnO12,
        model: row.model,
        trim: row.trim // currently is returned just as a string
      };

      // Object values - no object values in these results.
      const objects = {
      };

      // Get the text/value from the default culture
      Object.entries(objects).forEach(([j, prop]) => {
        prop.translations.forEach((culture, k) => {
          if (culture.defaultCulture === true) {
            rows[i][j] = culture.text;
          }
        });
      });
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const occurrenceDescriptionCore_ = occurrenceDescriptionCore();
    const occurrenceDescriptionFilter_ = occurrenceDescriptionFilter();

    const salesTypeNameCore_ = salesTypeNameCore();
    const salesTypeNameFilter_ = salesTypeNameFilter();

    const commonStatusPointCore_ = commonStatusPointCore();
    const commonStatusPointFilter_ = commonStatusPointFilter();

    const brandStatusDescriptionCore_ = brandStatusDescriptionCore();
    const brandStatusDescriptionFilter_ = brandStatusDescriptionFilter();

    return [
      [
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['MetaOrderSearchType', 'MetaOrder']}
              label="Common Order #"
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <Filter
              type="multiselect"
              parameter={occurrenceDescriptionFilter_.parameter}
              label="Occurrence Description"
              endpoint={occurrenceDescriptionCore_.endpoint}
              getRows={occurrenceDescriptionFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6},
          component: (
            <Filter
              type="select"
              parameter="CountryTwoLetters"
              label="Country"
              isPlaceholderRendered={false}
              staticData={{
                data: [
                  {
                    label: 'United States',
                    value: 'US'
                  },
                  {
                    label: 'Canada',
                    value: 'CA'
                  }
                ]
              }}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <Filter
              type="multiselect"
              parameter={commonStatusPointFilter_.parameter}
              label="Common Status Point"
              endpoint={commonStatusPointCore_.endpoint}
              getRows={commonStatusPointFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <Filter
              type="multiselect"
              parameter={brandStatusDescriptionFilter_.parameter}
              label="Brand Status Description"
              endpoint={brandStatusDescriptionCore_.endpoint}
              getRows={brandStatusDescriptionFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <Filter
              type="multiselect"
              parameter={salesTypeNameFilter_.parameter}
              label="Sales Type Name"
              endpoint={salesTypeNameCore_.endpoint}
              getRows={salesTypeNameFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <Filter
              type="multiselect"
              parameter={salesTypeNameFilter_.secondHandoverParameter}
              label="Sales Type Second Handover Name"
              endpoint={salesTypeNameCore_.endpoint}
              getRows={salesTypeNameFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['PinNumberSearchType', 'PinNumber']}
              label="PIN"
            />
          )
        },
        {
          size: { xs: 12, md: 12, lg: 6 },
          component: (
            <Filter
              type="duo"
              parameter={['VinSearchType', 'Vin']}
              label="VIN"
            />
          )
        },
      ]
    ];
  };

  const getExportFilters = () => {
    // Taxonomies
    const occurrenceDescriptionCore_ = occurrenceDescriptionCore();
    const occurrenceDescriptionFilter_ = occurrenceDescriptionFilter();

    const salesTypeNameCore_ = salesTypeNameCore();
    const salesTypeNameFilter_ = salesTypeNameFilter();

    const commonStatusPointCore_ = commonStatusPointCore();
    const commonStatusPointFilter_ = commonStatusPointFilter();

    const brandStatusDescriptionCore_ = brandStatusDescriptionCore();
    const brandStatusDescriptionFilter_ = brandStatusDescriptionFilter();

    const modelCore_ = modelCore();
    const modelFilter_ = modelFilter();

    const modelYearCore_ = modelYearCore();
    const modelYearFilter_ = modelYearFilter();

    return [
      [
        {
          size: { xs: 12, lg: 12},
          component: (
            <ExportFilter
              type="select"
              parameter="CountryTwoLetters"
              label="Country"
              isPlaceholderRendered={false}
              staticData={{
                data: [
                  {
                    label: 'United States',
                    value: 'US'
                  },
                  {
                    label: 'Canada',
                    value: 'CA'
                  }
                ]
              }}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="datepicker"
              parameter="lastAmendedDateStart"
              label="Last Amended Date Start"
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="datepicker"
              parameter="lastAmendedDateEnd"
              label="Last Amended Date End"
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="datepicker"
              parameter="CustomerHandoverDateStart"
              label="Customer Handover Start Date"
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="datepicker"
              parameter="CustomerHandoverDateEnd"
              label="Customer Handover End Date"
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="datepicker"
              parameter="SoldDateStart"
              label="Sold Reporting Date Start"
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="datepicker"
              parameter="SoldDateEnd"
              label="Sold Reporting Date End"
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="multiselect"
              parameter={modelYearFilter_.parameter}
              label="Model Year"
              endpoint={modelYearCore_.endpoint}
              getRows={modelYearFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="multiselect"
              parameter={modelFilter_.parameterForVistaOrder}
              label="Model Type"
              endpoint={modelCore_.endpointSimple}
              getRows={modelFilter_.getRowsForVistaOrder}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="multiselect"
              parameter={commonStatusPointFilter_.parameter}
              label="Common Status Point"
              endpoint={commonStatusPointCore_.endpoint}
              getRows={commonStatusPointFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="multiselect"
              parameter={brandStatusDescriptionFilter_.parameter}
              label="Brand Status Description"
              endpoint={brandStatusDescriptionCore_.endpoint}
              getRows={brandStatusDescriptionFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="multiselect"
              parameter={salesTypeNameFilter_.parameter}
              label="Sales Type Name"
              endpoint={salesTypeNameCore_.endpoint}
              getRows={salesTypeNameFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="multiselect"
              parameter={salesTypeNameFilter_.secondHandoverParameter}
              label="Sales Type Second Handover Name"
              endpoint={salesTypeNameCore_.endpoint}
              getRows={salesTypeNameFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="multiselect"
              parameter={occurrenceDescriptionFilter_.parameter}
              label="Occurrence Description"
              endpoint={occurrenceDescriptionCore_.endpoint}
              getRows={occurrenceDescriptionFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, lg: 6 },
          component: (
            <ExportFilter
              type="duo"
              parameter={['PinNumberSearchType', 'PinNumber']}
              label="PIN"
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters,
    getExportFilters: getExportFilters
  };
}

// Vista order detail
export function vistaOrderDetail() {
  const baseUrl = '/vista-order';

  const getInputs = (data = null) => {
    let detailObj = {
      vistaOrderID: {
        inputValue: '',
        label: 'Vista Order ID',
        type: 'text'
      },
      commonOrderNumber: {
        inputValue: '',
        label: 'Common Order Number',
        type: 'text'
      },
      occurrenceCode: {
        inputValue: '',
        label: 'Occurrence Code',
        type: 'text'
      },
      occurrenceDescription: {
        inputValue: '',
        label: 'Occurrence Description',
        type: 'text'
      },
      lastAmendedDate: {
        inputValue: '',
        label: 'Last Amended Date',
        type: 'datetime'
      },
      commonStatusPoint: {
        inputValue: '',
        label: 'Common Status Point',
        type: 'text'
      },
      brandStatusDescription: {
        inputValue: '',
        label: 'Brand Status Description',
        type: 'text'
      },
      dealerOrderNumber: {
        inputValue: '',
        label: 'Dealer Order Number',
        type: 'text'
      },
      factoryOrderNumber: {
        inputValue: '',
        label: 'Factory Order Number',
        type: 'text'
      },
      country: {
        inputValue: '',
        label: 'Country',
        type: 'text'
      },
      year: {
        inputValue: '',
        label: 'Model Year',
        type: 'text'
      },
      salesTypeCode: {
        inputValue: '',
        label: 'Sales Type Code',
        type: 'text'
      },
      salesTypeName: {
        inputValue: '',
        label: 'Sales Type Name',
        type: 'text'
      },
      salesTypeSecondHandoverCode: {
        inputValue: '',
        label: 'Sales Type Second Handover Code',
        type: 'text'
      },
      salesTypeSecondHandoverName: {
        inputValue: '',
        label: 'Sales Type Second Handover Name',
        type: 'text'
      },
      pinNumber: {
        inputValue: '',
        label: 'Pin Number',
        type: 'text'
      },
      vin: {
        inputValue: '',
        label: 'VIN',
        type: 'text'
      },
      shortVin: {
        inputValue: '',
        label: 'Short VIN',
        type: 'text'
      },
      soldReportingDate: {
        inputValue: '',
        label: 'Sold Reporting Date',
        type: 'datetime'
      },
      dealerCode: {
        inputValue: '',
        label: 'Dealer Code',
        type: 'text'
      },
      vpid: {
        inputValue: '',
        label: 'VPID',
        type: 'text'
      },
      pnO12: {
        inputValue: '',
        label: 'PNO12',
        type: 'text'
      },
      model: {
        inputValue: '',
        label: 'Model',
        type: 'text'
      },
      trim: {
        inputValue: '',
        label: 'Trim',
        type: 'text'
      },
      upholsteryCode: {
        inputValue: '',
        label: 'Upholstery Code',
        type: 'text'
      },
      exteriorColorCode: {
        inputValue: '',
        label: 'Exterior Color Code',
        type: 'text'
      },
      packageCode: {
        inputValue: '',
        label: 'Package Code',
        type: 'text'
      },
      accessoryOptionCode: {
        inputValue: '',
        label: 'Accessory Option Code',
        type: 'text'
      },
      standardFeaturesCode: {
        inputValue: '',
        label: 'Standard Features Code',
        type: 'text'
      },
      maketCode: {
        inputValue: '',
        label: 'Maket Code',
        type: 'text'
      },
      brand: {
        inputValue: '',
        label: 'Brand',
        type: 'text'
      },
      bySystem: {
        inputValue: '',
        label: 'By System',
        type: 'text'
      },
      brandEventTimeStamp: {
        inputValue: '',
        label: 'Brand Event Time Stamp',
        type: 'datetime'
      },
      brandProcessTimeStamp: {
        inputValue: '',
        label: 'Brand Process Time Stamp',
        type: 'datetime'
      },
      locationFrom: {
        inputValue: '',
        label: 'Location Form',
        type: 'text'
      },
      vistaProcessTimeStamp: {
        inputValue: '',
        label: 'Vista Process Time Stamp',
        type: 'text'
      },
      dealerInvoiceDate: {
        inputValue: '',
        label: 'Dealer Invoice Date',
        type: 'datetime'
      },
      brandOrderType: {
        inputValue: '',
        label: 'Brand Order Type',
        type: 'text'
      },
      bulkAmendReference: {
        inputValue: '',
        label: 'Bulk Amend Reference',
        type: 'text'
      },
      buyerCode: {
        inputValue: '',
        label: 'Buyer Code',
        type: 'text'
      },
      deliveredDate: {
        inputValue: '',
        label: 'Delivered Date',
        type: 'datetime'
      },
      distributorUniqueId: {
        inputValue: '',
        label: 'Distributor Unique ID',
        type: 'text'
      },
      financialStatus: {
        inputValue: '',
        label: 'Financial Status',
        type: 'text'
      },
      vccProgramMarket: {
        inputValue: '',
        label: 'VCC Program Market',
        type: 'text'
      },
      partnerDestinationCode: {
        inputValue: '',
        label: 'Partner Destination Code',
        type: 'text'
      },
      actualFactoryCompleteDate: {
        inputValue: '',
        label: 'Actual Factory Complete Date',
        type: 'datetime'
      },
      factoryCode: {
        inputValue: '',
        label: 'Factory Code',
        type: 'text'
      },
      lastDayForSpecificationChange: {
        inputValue: '',
        label: 'Last Day For Specification Change',
        type: 'datetime'
      },
      plannedFactoryCompleteDate: {
        inputValue: '',
        label: 'Planned Factory Complete Date',
        type: 'datetime'
      },
      acceptedBuildDate: {
        inputValue: '',
        label: 'Accepted Build Date',
        type: 'datetime'
      },
      acceptedDeliveryDate: {
        inputValue: '',
        label: 'Accepted Delivery Date',
        type: 'datetime'
      },
      asapIndicator: {
        inputValue: '',
        label: 'ASAP Indicator',
        type: 'text'
      },
      currentPlannedBuildDate: {
        inputValue: '',
        label: 'Current Planned Build Date',
        type: 'datetime'
      },
      currentPlannedDeliveryDate: {
        inputValue: '',
        label: 'Current Planned Delivery Date',
        type: 'datetime'
      },
      structureWeek: {
        inputValue: '',
        label: 'Structure Week',
        type: 'text'
      },
      actualDateToIntermediateDestination: {
        inputValue: '',
        label: 'Actual Date to Intermediate Destination',
        type: 'datetime'
      },
      availableForUnconcTradeInd: {
        inputValue: '',
        label: 'Available for Unconc Trade Ind',
        type: 'text'
      },
      commonOrderType: {
        inputValue: '',
        label: 'Common Order Type',
        type: 'text'
      },
      frozenIndicator: {
        inputValue: '',
        label: 'Frozen Indicator',
        type: 'text'
      },
      intermediateDestinationCode: {
        inputValue: '',
        label: 'Intermediate Destination Code',
        type: 'text'
      },
      lastConfirmationResponseDate: {
        inputValue: '',
        label: 'Last Confirmation Response Date',
        type: 'datetime'
      },
      orderConfirmedDate: {
        inputValue: '',
        label: 'Order Confirmed Date',
        type: 'datetime'
      },
      orderCreatedDate: {
        inputValue: '',
        label: 'Order Created Date',
        type: 'datetime'
      },
      pendingTransferInd: {
        inputValue: '',
        label: 'Pendind Transfer ID',
        type: 'text'
      },
      plannedDateToIntermediateDestination: {
        inputValue: '',
        label: 'Planned Date to Intermediate Destination',
        type: 'datetime'
      },
      sourceSystem: {
        inputValue: '',
        label: 'Source System',
        type: 'text'
      },
      visibilityInd: {
        inputValue: '',
        label: 'Visibility Ind',
        type: 'text'
      },
      wholesaleOrderDate: {
        inputValue: '',
        label: 'Wholesale Order Date',
        type: 'datetime'
      },
      reservedIndicator: {
        inputValue: '',
        label: 'Reserved Indicator',
        type: 'text'
      },
      chassisNumber: {
        inputValue: '',
        label: 'Chassis Number',
        type: 'text'
      },
      engineNumber: {
        inputValue: '',
        label: 'Engine Number',
        type: 'text'
      },
      electricalEngineRearLeft: {
        inputValue: '',
        label: 'Electrical Engine Rear Left',
        type: 'text'
      },
      electricalEngineRearRight: {
        inputValue: '',
        label: 'Electrical Engine Rear Right',
        type: 'text'
      },
      tyreMake: {
        inputValue: '',
        label: 'Tyre Make',
        type: 'text'
      },
      doorKey: {
        inputValue: '',
        label: 'Door Key',
        type: 'text'
      },
      ignitionKey: {
        inputValue: '',
        label: 'Ignition Key',
        type: 'text'
      },
      calloverIndicator: {
        inputValue: '',
        label: 'Callover Indicator',
        type: 'text'
      },
      //
      alertText: {
        inputValue: '',
        label: 'Alert Text',
        type: 'text'
      },
      changeType: {
        inputValue: '',
        label: 'Change Type',
        type: 'text'
      },
      dataUpdate: {
        inputValue: '',
        label: 'Data Update',
        type: 'text'
      },
      locationTo: {
        inputValue: '',
        label: 'Location To',
        type: 'text'
      },
      reasonCode: {
        inputValue: '',
        label: 'Reason Code',
        type: 'text'
      },
      customerHandoverDate: {
        inputValue: '',
        label: 'Customer Handover Date',
        type: 'text'
      },
      bulkReference: {
        inputValue: '',
        label: 'Bulk Reference',
        type: 'text'
      },
      dealerReferenceNumber1: {
        inputValue: '',
        label: 'Dealer Reference Number 1',
        type: 'text'
      },
      dealerReferenceNumber2: {
        inputValue: '',
        label: 'Dealer Reference Number 2',
        type: 'text'
      },
      dealerReferenceNumber3: {
        inputValue: '',
        label: 'Dealer Reference Number 3',
        type: 'text'
      },
      deliveringDealer: {
        inputValue: '',
        label: 'Delivering Dealer',
        type: 'text'
      },
      financialReference1: {
          inputValue: '',
          label: 'Financial Reference 1',
          type: 'text'
      },
      financialReference2: {
          inputValue: '',
          label: 'Financial Reference 2',
          type: 'text'
      },
      financialReference3: {
          inputValue: '',
          label: 'Financial Reference 3',
          type: 'text'
      },
      invoicedDealer: {
          inputValue: '',
          label: 'Invoiced Dealer',
          type: 'text'
      },
      nscReferenceNumber1: {
          inputValue: '',
          label: 'NSC Reference Number 1',
          type: 'text'
      },
      nscReferenceNumber2: {
          inputValue: '',
          label: 'NSC Reference Number 2',
          type: 'text'
      },
      nscReferenceNumber3: {
          inputValue: '',
          label: 'NSC Reference Number 3',
          type: 'text'
      },
      requestedBuildDate: {
          inputValue: '',
          label: 'Requested Build Date',
          type: 'text'
      },
      requestedDeliveryDate: {
          inputValue: '',
          label: 'Requested Delivery Date',
          type: 'text'
      },
      owningPartnerUniqueId: {
          inputValue: '',
          label: 'Owning Partner Unique ID',
          type: 'text'
      },
      nscOrderNumber: {
          inputValue: '',
          label: 'NSC Order Number',
          type: 'text'
      },
      holdAction: {
          inputValue: '',
          label: 'Hold Action',
          type: 'text'
      },
      holdsCode: {
          inputValue: '',
          label: 'Holds Code',
          type: 'text'
      },
      holdsDescription: {
          inputValue: '',
          label: 'Holds Description',
          type: 'text'
      },
      holdsPlannedReleaseDate: {
          inputValue: '',
          label: 'Holds Planned Release Date',
          type: 'text'
      },
      holdsBrandProcessDateTime: {
          inputValue: '',
          label: 'Holds Brand Process Date Time',
          type: 'text'
      },
      holdsOrganisationLevel: {
          inputValue: '',
          label: 'Holds Organisation Level',
          type: 'text'
      },
      firstFactoryComplete: {
          inputValue: '',
          label: 'First Factory Complete',
          type: 'text'
      },
      allocationPeriodStartDate: {
          inputValue: '',
          label: 'Allocation Period Start Date',
          type: 'text'
      },
      allocationPeriodEndDate: {
          inputValue: '',
          label: 'Allocation Period End Date',
          type: 'text'
      },
      firmDateRequirementIndicator: {
          inputValue: '',
          label: 'Firm Date Requirement Indicator',
          type: 'text'
      },
      schedulingRequestedBuildDate: {
          inputValue: '',
          label: 'Scheduling Requested Build Date',
          type: 'text'
      },
      marketCode: {
          inputValue: '',
          label: 'Market Code',
          type: 'text'
      },
      dealerOrderEntryDate: {
          inputValue: '',
          label: 'Dealer Order Entry Date',
          type: 'text'
      },
      latestCustPromiseHandoverDate: {
          inputValue: '',
          label: 'Latest Cust Promise Handover Date',
          type: 'text'
      },
      manufacturerListPrice: {
          inputValue: '',
          label: 'Manufacturer List Price',
          type: 'text'
      },
      numberOfLoanDays: {
          inputValue: '',
          label: 'Number of Loan Days',
          type: 'text'
      },
      orderCancelDate: {
          inputValue: '',
          label: 'Order Cancel Date',
          type: 'text'
      },
      wholesalePriceDate: {
          inputValue: '',
          label: 'Wholesale Price Date',
          type: 'text'
      },
      publicNote: {
          inputValue: '',
          label: 'Public Note',
          type: 'text'
      },
      privateNote: {
          inputValue: '',
          label: 'Private Note',
          type: 'text'
      },
      nscNote: {
          inputValue: '',
          label: 'NSC Note',
          type: 'text'
      },
      registrationDate: {
          inputValue: '',
          label: 'Registration Date',
          type: 'text'
      },
      registrationNo: {
          inputValue: '',
          label: 'Registration No',
          type: 'text'
      },
      registrationType: {
          inputValue: '',
          label: 'Registration Type',
          type: 'text'
      },
      registrationFees: {
          inputValue: '',
          label: 'Registration Fees',
          type: 'text'
      },
      vehicleExciseDuty: {
          inputValue: '',
          label: 'Vehicle Excise Duty',
          type: 'text'
      },
      reservedPartner: {
          inputValue: '',
          label: 'Reserved Partner',
          type: 'text'
      },
      descriptionGroupId: {
          inputValue: '',
          label: 'Description Group ID',
          type: 'text'
      },
      electricEngineNumber: {
          inputValue: '',
          label: 'Electric Engine Number',
          type: 'text'
      },
      electricalEngineFront: {
          inputValue: '',
          label: 'Electrical Engine Front',
          type: 'text'
      },
      electricalEngineFrontLeft: {
          inputValue: '',
          label: 'Electrical Engine Front Left',
          type: 'text'
      },
      electricalEngineFrontRight: {
          inputValue: '',
          label: 'Electrical Engine Front Right',
          type: 'text'
      },
      electricalEngineRear: {
          inputValue: '',
          label: 'Electrical Engine Rear',
          type: 'text'
      },
      hybridElectricalEngine: {
          inputValue: '',
          label: 'Hybrid Electrical Engine',
          type: 'text'
      },
      batteryCode: {
          inputValue: '',
          label: 'Battery Code',
          type: 'text'
      },
      confirmedDeliveryDateTime: {
          inputValue: '',
          label: 'Confirmed Delivery Date Time',
          type: 'text'
      },
      contractCancelledDate: {
          inputValue: '',
          label: 'Contract Cancelled Date',
          type: 'text'
      },
      contractCancelledIndicator: {
          inputValue: '',
          label: 'Contract Cancelled Indicator',
          type: 'text'
      },
      contractDate: {
          inputValue: '',
          label: 'Contract Date',
          type: 'text'
      },
      contractNumber: {
          inputValue: '',
          label: 'Contract Number',
          type: 'text'
      },
      customerOrderReferenceNumber: {
          inputValue: '',
          label: 'Customer Order Reference Number',
          type: 'text'
      },
      customerPromisedHandoverDate: {
          inputValue: '',
          label: 'Customer Promised Handover Date',
          type: 'text'
      },
      employeeName: {
          inputValue: '',
          label: 'Employee Name',
          type: 'text'
      },
      employeeNumber: {
          inputValue: '',
          label: 'Employee Number',
          type: 'text'
      },
      estimateAnnualMileage: {
          inputValue: '',
          label: 'Estimate Annual Mileage',
          type: 'text'
      },
      estimatedHandoverWeek: {
          inputValue: '',
          label: 'Estimated Handover Week',
          type: 'text'
      },
      freeTextNotes: {
          inputValue: '',
          label: 'Free Text Notes',
          type: 'text'
      },
      mileage: {
          inputValue: '',
          label: 'Mileage',
          type: 'text'
      },
      nominatedUser: {
          inputValue: '',
          label: 'Nominated User',
          type: 'text'
      },
      opportunityID: {
          inputValue: '',
          label: 'Opportunity ID',
          type: 'text'
      },
      outOfServiceDate: {
          inputValue: '',
          label: 'Out of Service Date',
          type: 'text'
      },
      paymentType: {
          inputValue: '',
          label: 'Payment Type',
          type: 'text'
      },
      predictedReplacementDate: {
          inputValue: '',
          label: 'Predicted Replacement Date',
          type: 'text'
      },
      predictedReplacementMileage: {
          inputValue: '',
          label: 'Predicted Replacement Mileage',
          type: 'text'
      },
      primaryInternalCustomer: {
          inputValue: '',
          label: 'Primary Internal Customer',
          type: 'text'
      },
      purchaseOrderNumber: {
          inputValue: '',
          label: 'Purchase Order Number',
          type: 'text'
      },
      replacedRegistrationNumber: {
          inputValue: '',
          label: 'Replaced Registration Number',
          type: 'text'
      },
      salesman: {
          inputValue: '',
          label: 'Salesman',
          type: 'text'
      },
      salesmanName: {
          inputValue: '',
          label: 'Salesman Name',
          type: 'text'
      },
      contractSalesTypeCode: {
          inputValue: '',
          label: 'Contract Sales Type Code',
          type: 'text'
      },
      contractSourceSystem: {
          inputValue: '',
          label: 'Contract Source System',
          type: 'text'
      },
      vehicleForCollection: {
          inputValue: '',
          label: 'Vehicle For Collection',
          type: 'text'
      },
      sellingPartnerId: {
          inputValue: '',
          label: 'Selling Partner ID',
          type: 'text'
      },
      massInRunningOrderTotalRedaName: {
          inputValue: '',
          label: 'Mass In Running Order Total Reda Name',
          type: 'text'
      },
      massInRunningOrderTotal: {
          inputValue: '',
          label: 'Mass In Running Order Total',
          type: 'text'
      },
      massInRunningOrderTotalUnit: {
          inputValue: '',
          label: 'Mass In Running Order Total Unit',
          type: 'text'
      },
      testMassIndRedaName: {
          inputValue: '',
          label: 'Test Mass Ind Reda Name',
          type: 'text'
      },
      testMassInd: {
          inputValue: '',
          label: 'Test Mass Ind',
          type: 'text'
      },
      testMassIndUnit: {
          inputValue: '',
          label: 'Test Mass Ind Unit',
          type: 'text'
      },
      actualMassRedaName: {
          inputValue: '',
          label: 'Actual Mass Reda Name',
          type: 'text'
      },
      actualMass: {
          inputValue: '',
          label: 'Actual Mass',
          type: 'text'
      },
      actualMassUnit: {
          inputValue: '',
          label: 'Actual Mass Unit',
          type: 'text'
      },
      tpMaxLadenMassRedaName: {
          inputValue: '',
          label: 'TP Max Laden Mass Reda Name',
          type: 'text'
      },
      tpMaxLadenMass: {
          inputValue: '',
          label: 'TP Max Laden Mass',
          type: 'text'
      },
      tpMaxLadenMassUnit: {
          inputValue: '',
          label: 'TP Max Laden Mass Unit',
          type: 'text'
      }
    };

    if (data !== null) {
      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj).forEach(([i, prop]) => {
        detailObj[i].inputValue = data[i] === null ? '' : data[i];
      });
    }

    let inputsResult = [];
    // Use the data provided to populate the inputs result.
    Object.entries(detailObj).forEach(([i, prop]) => {
      inputsResult.push([
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type={detailObj[i].type}
              disabled={true}
              parameter={i}
              label={detailObj[i].label}
              inputValue={detailObj[i].inputValue}
            />
          )
        }
      ]);
    });

    return inputsResult;
  };

  return {
    baseUrl,
    getInputs: getInputs
  };
}
